import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';

import localUtil from '../../lib/util';
import { appAlert } from '../../lib/dialogs';

@observer
class WeaponTestDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            updateInProgress: {},
            weaponTest: {
                testDate: new Date().toISOString().split('T')[0],
                speedUnit: 'fps',
                accuracyUnit: 'MOA',
                distanceUnit: 'm',
            },
        };
        this.updateTimers = {};
    }

    componentDidMount() {
        const { testUuidv4, isNew, isNewLotTesting } = this.props;
        const { lotTestingStore } = this.props.stores;

        // Determine which object to look in for existing tests
        const lotTesting = isNewLotTesting
            ? (lotTestingStore.newLotTesting || {})
            : (lotTestingStore.lotTesting || {});

        // If editing (testUuidv4 provided), load existing test data
        if (!isNew && testUuidv4) {
            const weaponTests = lotTesting.weaponTests || [];
            const existingTest = weaponTests.find(test => test.uuidv4 === testUuidv4);
            if (existingTest) {
                // Format the date for the input field
                let testDate = existingTest.testDate;
                if (testDate) {
                    testDate = new Date(testDate).toISOString().split('T')[0];
                }
                this.setState({
                    weaponTest: {
                        ...existingTest,
                        testDate: testDate || new Date().toISOString().split('T')[0],
                        speedUnit: existingTest.speedUnit || 'fps',
                        accuracyUnit: existingTest.accuracyUnit || 'MOA',
                        distanceUnit: existingTest.distanceUnit || 'm',
                    },
                });
            }
        }
    }

    setUpdateInProgress = (fieldKey, inProgress) => {
        this.setState(prevState => ({
            updateInProgress: {
                ...prevState.updateInProgress,
                [fieldKey]: inProgress,
            },
        }));
    }

    handleFieldChange = (e) => {
        const { name, value, type } = e.target;
        const { isNew, lotTestingId, testUuidv4, isNewLotTesting } = this.props;

        // Update local state immediately
        this.setState(prevState => ({
            weaponTest: {
                ...prevState.weaponTest,
                [name]: value,
            },
        }));

        // Auto-save with 750ms delay when editing existing test on an existing LOT entry
        // (not adding new, and not editing a new LOT entry that hasn't been saved yet)
        if (!isNew && testUuidv4 && !isNewLotTesting) {
            this.setUpdateInProgress(name, true);

            // Clear existing timer for this field
            if (this.updateTimers[name]) {
                clearTimeout(this.updateTimers[name]);
            }

            // Capture values for the timeout closure
            const capturedName = name;
            const capturedValue = value;
            const capturedType = type;

            // Set new timer for auto-save
            this.updateTimers[name] = setTimeout(() => {
                this.doAutoSave({ name: capturedName, value: capturedValue, type: capturedType });
            }, 750);
        }
    }

    doAutoSave = async ({ name, value, type }) => {
        const { lotTestingId, testUuidv4 } = this.props;
        const { lotTestingStore } = this.props.stores;

        await lotTestingStore.editArrayObject({
            field: 'weaponTests',
            key: name,
            value,
            type,
            id: lotTestingId,
            uuidv4: testUuidv4,
            objectName: 'lotTesting',
        });

        this.setUpdateInProgress(name, false);
    }

    getDeviceLocation = () => {
        const { isNew, testUuidv4, isNewLotTesting } = this.props;

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    this.setState(prevState => ({
                        weaponTest: {
                            ...prevState.weaponTest,
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                        },
                    }));

                    // Auto-save GPS coordinates when editing existing LOT entry
                    if (!isNew && testUuidv4 && !isNewLotTesting) {
                        this.setUpdateInProgress('latitude', true);
                        this.setUpdateInProgress('longitude', true);

                        // Save latitude
                        setTimeout(() => {
                            this.doAutoSave({ name: 'latitude', value: position.coords.latitude });
                        }, 100);

                        // Save longitude
                        setTimeout(() => {
                            this.doAutoSave({ name: 'longitude', value: position.coords.longitude });
                        }, 200);
                    }
                },
                (error) => {
                    console.error('Geolocation error:', error);
                    void appAlert('Could not get your location. Please enter coordinates manually.');
                }
            );
        } else {
            void appAlert('Geolocation is not supported by this browser.');
        }
    }

    saveWeaponTest = async () => {
        const { lotTestingId, callback, isNewLotTesting } = this.props;
        const { lotTestingStore, appState } = this.props.stores;
        const { weaponTest } = this.state;

        if (isNewLotTesting) {
            // Add to local state for new LOT entries (not yet saved to API)
            lotTestingStore.addWeaponTestToNew(weaponTest);
        } else {
            // Add via API for existing LOT entries
            await lotTestingStore.addWeaponTest(lotTestingId, weaponTest);
        }

        if (callback) {
            callback();
        }
        appState.toggleDrawer();
    }

    closeDrawer = () => {
        const { callback, isNewLotTesting, testUuidv4 } = this.props;
        const { lotTestingStore, appState } = this.props.stores;
        const { weaponTest } = this.state;

        // For new LOT entries being edited, update local state when closing
        if (isNewLotTesting && testUuidv4) {
            const currentTests = lotTestingStore.newLotTesting.weaponTests || [];
            const updatedTests = currentTests.map(test =>
                test.uuidv4 === testUuidv4 ? { ...weaponTest, uuidv4: testUuidv4 } : test
            );
            lotTestingStore.updateObjectKeyValue('newLotTesting', 'weaponTests', updatedTests);
        }

        if (callback) {
            callback();
        }
        appState.toggleDrawer();
    }

    render() {
        const { isNew, isNewLotTesting, drawerLevel = 1 } = this.props;
        const { weaponTest, updateInProgress = {} } = this.state;
        const isSaving = Object.values(updateInProgress).some(v => v);
        const showAutoSave = !isNew && !isNewLotTesting;

        return (
            <div class='weapon-test-drawer'>
                <div class='w-100 d-flex justify-content-center'>
                    <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>

                        <div class='font-weight-lighter px-3 box-header mt-0 d-flex justify-content-between align-items-center'>
                            <span>{isNew ? 'Add Weapon Test' : 'Edit Weapon Test'}</span>
                            {showAutoSave && isSaving && (
                                <small class='text-primary'>
                                    <i class='fa-solid fa-spinner fa-spin me-1' />
                                    Saving...
                                </small>
                            )}
                        </div>

                        <div class='px-3 mt-3'>
                            <div class='card'>
                                <div class='card-body'>
                                    <div class='row g-3'>
                                        {/* Weapon Info */}
                                        <div class='col-md-6'>
                                            <label class='form-label small'>Weapon Brand</label>
                                            <input
                                                type='text'
                                                class='form-control form-control-sm'
                                                name='weaponBrand'
                                                value={weaponTest.weaponBrand || ''}
                                                onInput={this.handleFieldChange}
                                                placeholder='e.g., Sig Sauer'
                                            />
                                        </div>
                                        <div class='col-md-6'>
                                            <label class='form-label small'>Weapon Model</label>
                                            <input
                                                type='text'
                                                class='form-control form-control-sm'
                                                name='weaponModel'
                                                value={weaponTest.weaponModel || ''}
                                                onInput={this.handleFieldChange}
                                                placeholder='e.g., P320'
                                            />
                                        </div>
                                        <div class='col-md-4'>
                                            <label class='form-label small'>Barrel Length (inches)</label>
                                            <input
                                                type='number'
                                                class='form-control form-control-sm'
                                                name='barrelLength'
                                                value={weaponTest.barrelLength || ''}
                                                onInput={this.handleFieldChange}
                                                step='0.1'
                                            />
                                        </div>
                                        <div class='col-md-4'>
                                            <label class='form-label small'>Test Date</label>
                                            <input
                                                type='date'
                                                class='form-control form-control-sm'
                                                name='testDate'
                                                value={weaponTest.testDate || ''}
                                                onInput={this.handleFieldChange}
                                            />
                                        </div>
                                        <div class='col-md-4'>
                                            <label class='form-label small'>Shot Count</label>
                                            <input
                                                type='number'
                                                class='form-control form-control-sm'
                                                name='shotCount'
                                                value={weaponTest.shotCount || ''}
                                                onInput={this.handleFieldChange}
                                                min='1'
                                                placeholder='# shots'
                                            />
                                        </div>

                                        {/* Distance */}
                                        <div class='col-md-4'>
                                            <label class='form-label small'>Distance</label>
                                            <div class='input-group input-group-sm'>
                                                <input
                                                    type='number'
                                                    class='form-control'
                                                    name='distance'
                                                    value={weaponTest.distance || ''}
                                                    onInput={this.handleFieldChange}
                                                />
                                                <select
                                                    class='form-select'
                                                    name='distanceUnit'
                                                    value={weaponTest.distanceUnit || 'm'}
                                                    onChange={this.handleFieldChange}
                                                    style='max-width: 80px;'
                                                >
                                                    <option value='m'>m</option>
                                                    <option value='yards'>yards</option>
                                                    <option value='feet'>feet</option>
                                                </select>
                                            </div>
                                        </div>

                                        {/* Indoor checkbox */}
                                        <div class='col-md-2 d-flex align-items-end'>
                                            <div class='form-check mb-2'>
                                                <input
                                                    type='checkbox'
                                                    class='form-check-input'
                                                    id='isIndoor'
                                                    name='isIndoor'
                                                    checked={weaponTest.isIndoor || false}
                                                    onChange={(e) => this.handleFieldChange({ target: { name: 'isIndoor', value: e.target.checked } })}
                                                />
                                                <label class='form-check-label small' for='isIndoor'>
                                                    <i class='fa-solid fa-warehouse me-1' />
                                                    Indoor
                                                </label>
                                            </div>
                                        </div>

                                        {/* Speed */}
                                        <div class='col-md-9'>
                                            <label class='form-label small'>Speed (min / avg / max)</label>
                                            <div class='input-group input-group-sm'>
                                                <input
                                                    type='number'
                                                    class='form-control'
                                                    name='speedMin'
                                                    value={weaponTest.speedMin || ''}
                                                    onInput={this.handleFieldChange}
                                                    placeholder='Min'
                                                />
                                                <input
                                                    type='number'
                                                    class='form-control'
                                                    name='speed'
                                                    value={weaponTest.speed || ''}
                                                    onInput={this.handleFieldChange}
                                                    placeholder='Avg'
                                                />
                                                <input
                                                    type='number'
                                                    class='form-control'
                                                    name='speedMax'
                                                    value={weaponTest.speedMax || ''}
                                                    onInput={this.handleFieldChange}
                                                    placeholder='Max'
                                                />
                                                <select
                                                    class='form-select'
                                                    name='speedUnit'
                                                    value={weaponTest.speedUnit || 'fps'}
                                                    onChange={this.handleFieldChange}
                                                    style='max-width: 80px;'
                                                >
                                                    <option value='fps'>fps</option>
                                                    <option value='m/s'>m/s</option>
                                                </select>
                                            </div>
                                            {weaponTest.speedMin && weaponTest.speedMax && (
                                                <small class='text-muted'>
                                                    ES: {weaponTest.speedMax - weaponTest.speedMin} {weaponTest.speedUnit || 'fps'}
                                                </small>
                                            )}
                                        </div>
                                        <div class='col-md-3'>
                                            <label class='form-label small'>Std.Dev</label>
                                            <input
                                                type='number'
                                                class='form-control form-control-sm'
                                                name='speedStdDev'
                                                value={weaponTest.speedStdDev || ''}
                                                onInput={this.handleFieldChange}
                                                step='0.1'
                                                placeholder='SD'
                                            />
                                        </div>

                                        {/* Accuracy */}
                                        <div class='col-md-6'>
                                            <label class='form-label small'>Accuracy</label>
                                            <div class='input-group input-group-sm'>
                                                <input
                                                    type='number'
                                                    class='form-control'
                                                    name='accuracy'
                                                    value={weaponTest.accuracy || ''}
                                                    onInput={this.handleFieldChange}
                                                    step='0.01'
                                                />
                                                <select
                                                    class='form-select'
                                                    name='accuracyUnit'
                                                    value={weaponTest.accuracyUnit || 'MOA'}
                                                    onChange={this.handleFieldChange}
                                                    style='max-width: 80px;'
                                                >
                                                    <option value='MOA'>MOA</option>
                                                    <option value='inches'>inches</option>
                                                </select>
                                            </div>
                                        </div>

                                        {/* GPS Location */}
                                        <div class='col-md-6'>
                                            <label class='form-label small'>GPS Location</label>
                                            <div class='input-group input-group-sm'>
                                                <input
                                                    type='number'
                                                    class='form-control'
                                                    name='latitude'
                                                    value={weaponTest.latitude || ''}
                                                    onInput={this.handleFieldChange}
                                                    placeholder='Lat'
                                                    step='0.0001'
                                                />
                                                <input
                                                    type='number'
                                                    class='form-control'
                                                    name='longitude'
                                                    value={weaponTest.longitude || ''}
                                                    onInput={this.handleFieldChange}
                                                    placeholder='Lon'
                                                    step='0.0001'
                                                />
                                                <button
                                                    type='button'
                                                    class='btn btn-outline-secondary'
                                                    onClick={this.getDeviceLocation}
                                                    title='Get current location'
                                                >
                                                    <i class='fa-solid fa-location-crosshairs' />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Notes */}
                                        <div class='col-12'>
                                            <label class='form-label small'>Notes</label>
                                            <textarea
                                                class='form-control form-control-sm'
                                                name='notes'
                                                value={weaponTest.notes || ''}
                                                onInput={this.handleFieldChange}
                                                rows='3'
                                                placeholder='Any notes about this test...'
                                            />
                                        </div>
                                    </div>

                                    {/* Auto-save message for editing existing LOT entries */}
                                    {showAutoSave && (
                                        <div class='mt-3 text-muted small'>
                                            <i class='fa-solid fa-info-circle me-1' />
                                            Changes are saved automatically
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* Action buttons */}
                <div class='w-100 d-flex flex-column justify-content-start mt-3 px-4 py-3 bg-light fixed-bottom' style='bottom: 0; left: 0;'>
                    <div class='d-flex justify-content-end'>
                        {isNew ? (
                            <button
                                class='btn btn-primary rounded-pill'
                                onClick={this.saveWeaponTest}
                            >
                                <i class='fa-solid fa-plus me-1' />
                                Add Test
                            </button>
                        ) : isNewLotTesting ? (
                            <button
                                class='btn btn-primary rounded-pill'
                                onClick={this.closeDrawer}
                            >
                                <i class='fa-solid fa-save me-1' />
                                Save Changes
                            </button>
                        ) : (
                            <button
                                class='btn btn-secondary rounded-pill'
                                onClick={this.closeDrawer}
                            >
                                <i class='fa-solid fa-check me-1' />
                                Done
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default WeaponTestDrawer;
